import React from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {
  Box,
  Chip,
  Link,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import {
  CANCER_TYPES,
  getCancerHistoriesArray,
  PATIENT_THEME, 
  Patient,
  ROLE_COLORS as PATIENT_ROLE_COLORS, 
  ROLE_TYPES as PATIENT_ROLE_TYPES
} from '../../types/patient';
import {GenderAgeDisplay, NameNicknameDisplay} from '../common';

interface PatientCardProps {
  patient: Patient | null | undefined;
}

const PatientCard: React.FC<PatientCardProps> = ({patient}) => {
  
  return (
    <Paper sx={{
      p: 3,
      bgcolor: PATIENT_THEME.LIST.BACKGROUND,
      borderRadius: 2,
      height: '100%',
      borderLeft: `4px solid ${PATIENT_THEME.LIST.BORDER}`,
      position: 'relative',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    }}>
      <Box sx={{mb: 2}}>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Typography variant="h5" fontWeight="medium" sx={{color: '#2E7D32'}}>
            患者
          </Typography>
          {patient && patient.role_type && (
            <Chip
              label={PATIENT_ROLE_TYPES[patient.role_type as keyof typeof PATIENT_ROLE_TYPES] || patient.role_type}
              size="small"
              sx={{ 
                ml: 1, 
                fontWeight: 'bold',
                backgroundColor: PATIENT_ROLE_COLORS[patient.role_type as keyof typeof PATIENT_ROLE_COLORS] || '#757575',
                color: 'white'
              }}
            />
          )}
        </Box>
      </Box>

      {patient ? (
        <>
          <Tooltip title="患者の詳細ページを表示" arrow placement="top">
            <Link
              component={RouterLink}
              to={`/patients/${patient.id}`}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                mb: 2,
                position: 'relative',
                padding: '8px 12px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 1px rgba(0,0,0,0.05)',
                '&:hover': {
                  backgroundColor: `${PATIENT_THEME.LIST.BACKGROUND}80`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                  '& .patient-name, & .patient-nickname': {
                    color: '#2E7D32',
                  },
                  '& .patient-detail-icon': {
                    opacity: 1,
                  }
                },
                '&:active': {
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  transform: 'translateY(0px)',
                }
              }}
            >
              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <NameNicknameDisplay
                  name={patient.name}
                  nickname={patient.nickname}
                  enableHoverEffect={true}
                  isTestAccount={patient.is_test_account}
                  sx={{ 
                    '& .name-hover': { transition: 'color 0.2s ease' },
                    '& .nickname-hover': { transition: 'color 0.2s ease' }
                  }}
                />
                <LinkIcon
                  sx={{
                    opacity: 0.5,
                    transition: 'all 0.3s ease',
                    fontSize: '1.2rem',
                    color: '#2E7D32'
                  }}
                  className="patient-detail-icon"
                />
              </Box>
            </Link>
          </Tooltip>

          <Box sx={{mb: 2}}>
            <Typography variant="body1" sx={{fontWeight: 'medium'}}>
              <GenderAgeDisplay 
                genderType={patient.gender_type || ''} 
                birthday={patient.birthday || ''}
              />
            </Typography>
          </Box>

          <Box sx={{mt: 2}}>
            <Typography variant="body2" sx={{color: 'text.secondary', mb: 1}}>
              がん疾患歴
            </Typography>
            {patient.cancer_histories ? (
              <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                {getCancerHistoriesArray(patient.cancer_histories).map((history, index) => (
                  <Chip
                    key={index}
                    label={
                      history.cancer_type === 'OTHER' && history.other
                        ? history.other
                        : history.cancer_type
                          ? CANCER_TYPES[history.cancer_type as keyof typeof CANCER_TYPES] || history.cancer_type
                          : '不明ながん種'
                    }
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{fontStyle: 'italic'}}>
                がん疾患歴の登録なし
              </Typography>
            )}
          </Box>
        </>
      ) : (
        <Typography color="text.secondary">
          患者情報がありません
        </Typography>
      )}
    </Paper>
  );
};

export default PatientCard;